import { useState } from 'react';
import type { ReactNode } from 'react';

/**
 * An image from the API, with a fallback that survives the URL going bad.
 *
 * Two ways that happens, and the second is the reason this component exists
 * rather than a bare `<img>`:
 *
 *  - The value is not an absolute http(s) URL at all. The console resolves
 *    Storage *paths* through the Firebase SDK; this site carries no Firebase,
 *    deliberately, so a path can only ever become a broken image.
 *  - The URL is a short-lived signed one (T9 signs speaker and sponsor assets
 *    so the bucket stays non-scrapable) and the tab has been open longer than
 *    the signature lives. A schedule left open on a phone overnight is the
 *    ordinary case, not the exotic one.
 *
 * Either way the fallback renders — initials, a name, a placeholder — and never
 * a broken-image glyph.
 */
export function RemoteImage({
  src,
  alt,
  className,
  fallback,
}: {
  src: string | null | undefined;
  alt: string;
  className?: string;
  fallback: ReactNode;
}) {
  const usable = src && /^https?:\/\//i.test(src) ? src : null;

  // Which URL failed, not merely *that* one did. A re-signed link arriving in a
  // refreshed payload is exactly how an expired image is meant to recover, and
  // a boolean would keep the fallback up for the life of the page — while an
  // effect that resets the boolean is a render-loop waiting to be written.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (!usable || failedSrc === usable) return <>{fallback}</>;

  return (
    <img
      src={usable}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={className}
      onError={() => setFailedSrc(usable)}
    />
  );
}
